import React, { useEffect, useId, useMemo, useRef } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import {
  FILE_VALIDATION,
  IMAGE_MODERATION_POSTURES,
  PUBLIC_LAUNCH_MODES,
  SETTINGS_CONFLICT_MALFORMED_VALUE,
} from '@find-my-part/types';
import type {
  ApiErrorResponse,
  StoreSettingsConflict,
  StoreSettingsConflictState,
  StoreSettingsExpectedState,
  StoreSettingsWriteField,
} from '@find-my-part/types';

const WRITE_FIELDS = new Set<StoreSettingsWriteField>([
  'publicLaunchMode',
  'submissionAcknowledgementEnabled',
  'imageAttachmentsEnabled',
  'maxImagesPerSubmission',
  'maxImagesPerReply',
  'maxImagesPerStaffReply',
  'maxImageSizeMb',
  'allowedImageFormats',
  'imageModeration',
]);

const CONFLICT_KEYS = new Set(['surface', 'contested', 'expected', 'observed', 'recovery']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isControlledConflictValue(field: StoreSettingsWriteField, value: unknown): boolean {
  if (value === SETTINGS_CONFLICT_MALFORMED_VALUE || value === null) return true;
  const validInt = (min: number, max: number): boolean =>
    typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
  switch (field) {
    case 'publicLaunchMode':
      return typeof value === 'string' && PUBLIC_LAUNCH_MODES.includes(value as never);
    case 'submissionAcknowledgementEnabled':
    case 'imageAttachmentsEnabled':
      return typeof value === 'boolean';
    case 'maxImagesPerSubmission':
      return validInt(1, FILE_VALIDATION.MAX_IMAGES_PER_QUESTION);
    case 'maxImagesPerReply':
      return validInt(0, FILE_VALIDATION.MAX_IMAGES_PER_REPLY);
    case 'maxImagesPerStaffReply':
      return validInt(0, FILE_VALIDATION.MAX_IMAGES_PER_STAFF_REPLY);
    case 'maxImageSizeMb':
      return validInt(1, FILE_VALIDATION.MAX_SIZE_MB);
    case 'allowedImageFormats':
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        JSON.stringify(value).length <= 4096 &&
        value.every(
          (member) =>
            typeof member === 'string' &&
            (FILE_VALIDATION.ALLOWED_MIME_TYPES as readonly string[]).includes(member)
        )
      );
    case 'imageModeration':
      return (
        typeof value === 'string' &&
        (IMAGE_MODERATION_POSTURES as readonly string[]).includes(value)
      );
  }
}

function readConflictState(value: unknown): StoreSettingsConflictState | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (
    keys.length === 0 ||
    keys.length > WRITE_FIELDS.size ||
    !keys.every(
      (field) =>
        WRITE_FIELDS.has(field as StoreSettingsWriteField) &&
        isControlledConflictValue(field as StoreSettingsWriteField, value[field])
    )
  ) {
    return null;
  }
  return Object.fromEntries(
    keys.map((field) => [field, value[field]])
  ) as StoreSettingsConflictState;
}

/** Strictly decode the bounded Worker conflict payload before the UI trusts it. */
export function readSettingsConflict(
  error: ApiErrorResponse['error']
): StoreSettingsConflict | null {
  if (error.code !== 'STALE_WRITE') return null;
  const value = error.details?.conflict;
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (!keys.every((key) => CONFLICT_KEYS.has(key)) || value.surface !== 'settings') return null;

  const expected = readConflictState(value.expected);
  const observed = readConflictState(value.observed);
  if (!expected || !observed) return null;
  const expectedKeys = Object.keys(expected) as StoreSettingsWriteField[];
  const observedKeys = Object.keys(observed);
  if (
    observedKeys.length !== expectedKeys.length ||
    !expectedKeys.every((field) => Object.prototype.hasOwnProperty.call(observed, field)) ||
    !Array.isArray(value.contested) ||
    value.contested.length === 0 ||
    value.contested.length > expectedKeys.length ||
    new Set(value.contested).size !== value.contested.length ||
    !value.contested.every(
      (field) =>
        typeof field === 'string' && expectedKeys.includes(field as StoreSettingsWriteField)
    )
  ) {
    return null;
  }

  const observedHasMalformed = Object.values(observed).some(
    (entry) => entry === SETTINGS_CONFLICT_MALFORMED_VALUE
  );
  // During a narrow Worker/UI skew, an older guarded Worker may omit the new
  // additive recovery member. It is safe to infer `direct` only when every
  // observed value passed the controlled-value decoder.
  const recovery = value.recovery === undefined ? 'direct' : value.recovery;
  if (
    (recovery !== 'direct' && recovery !== 'refresh_required') ||
    (recovery === 'direct' && observedHasMalformed) ||
    (recovery === 'refresh_required' && !observedHasMalformed)
  ) {
    return null;
  }

  return {
    surface: 'settings',
    contested: [...value.contested] as StoreSettingsWriteField[],
    expected,
    observed,
    recovery,
  };
}

/** Pick an exact, request-safe opaque baseline after a recovery refresh. */
export function selectUsableSettingsExpectedState(
  expectedState: StoreSettingsExpectedState | undefined,
  fields: readonly StoreSettingsWriteField[]
): Partial<StoreSettingsExpectedState> | null {
  if (!expectedState || fields.length === 0) return null;
  const selected: Partial<StoreSettingsExpectedState> = {};
  for (const field of fields) {
    if (!Object.prototype.hasOwnProperty.call(expectedState, field)) return null;
    const value = expectedState[field];
    const encoded = JSON.stringify(value);
    if (encoded === undefined || encoded.length > 4096) return null;
    selected[field] = value;
  }
  return selected;
}

export function isAmbiguousSettingsFailure(error: ApiErrorResponse['error']): boolean {
  return [
    'INTERNAL_ERROR',
    'NETWORK_ERROR',
    'REQUEST_TIMEOUT',
    'MAX_RETRIES_EXCEEDED',
    'SERVICE_UNAVAILABLE',
  ].includes(error.code);
}

interface SettingsConflictNoticeProps {
  conflict: StoreSettingsConflict | null;
  draft: Partial<Record<StoreSettingsWriteField, unknown>>;
  labels: Partial<Record<StoreSettingsWriteField, string>>;
  formatValue: (field: StoreSettingsWriteField, value: unknown) => string;
  busy: boolean;
  onReapply: () => void;
  onDismiss: () => void;
}

/**
 * The persistent alert announces only a static one-shot summary. The mutable
 * comparison and controls live in a separate named region, so editing a
 * preserved draft cannot retrigger assertive announcements.
 */
export function SettingsConflictNotice({
  conflict,
  draft,
  labels,
  formatValue,
  busy,
  onReapply,
  onDismiss,
}: SettingsConflictNoticeProps): React.ReactElement {
  const regionRef = useRef<HTMLDivElement>(null);
  const previousConflictRef = useRef<StoreSettingsConflict | null>(null);
  const announcementSequenceRef = useRef(0);
  const headingId = useId();
  if (previousConflictRef.current !== conflict) {
    previousConflictRef.current = conflict;
    if (conflict) announcementSequenceRef.current += 1;
  }
  useEffect(() => {
    if (conflict) regionRef.current?.focus();
  }, [conflict]);

  const converged = useMemo(() => {
    if (conflict === null) return false;
    // The guarded UPDATE is atomic across every attempted field. A conflict
    // is converged only when ALL original patch fields now equal the draft,
      // not merely the subset whose predicates were contested.
      const attempted = Object.keys(conflict.expected) as StoreSettingsWriteField[];
      return (
        attempted.length > 0 &&
        attempted.every(
        (field) => JSON.stringify(conflict.observed[field]) === JSON.stringify(draft[field])
      )
    );
  }, [conflict, draft]);

  return (
    <>
      <div
        role="alert"
        data-testid="settings-conflict-alert"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
        }}
      >
        {conflict && (
          <span key={announcementSequenceRef.current}>
            Another staff member changed these settings. Review the conflict notice.
          </span>
        )}
      </div>
      {conflict && (
        <div
          ref={regionRef}
          role="region"
          aria-labelledby={headingId}
          tabIndex={-1}
          data-testid="settings-conflict-notice"
          style={{ outline: 'none' }}
        >
          <Box
            backgroundColor="warning10"
            borderRadius="normal"
            padding="small"
            marginBottom="medium"
          >
            <Text id={headingId} bold marginBottom="xxSmall">
              Another staff member changed this since you loaded it.
            </Text>
            {(Object.keys(conflict.expected) as StoreSettingsWriteField[]).map((field) => (
            <Flex key={field} flexGap="1rem" flexWrap="wrap" marginBottom="xxSmall">
              <FlexItem>
                <Text marginBottom="none">
                  <strong>{labels[field] ?? field}</strong>
                </Text>
                </FlexItem>
                <FlexItem>
                  <Text marginBottom="none" data-testid={`settings-conflict-current-${field}`}>
                    Current:{' '}
                    {conflict.observed[field] === SETTINGS_CONFLICT_MALFORMED_VALUE
                      ? 'Stored value is invalid — refresh required'
                      : formatValue(field, conflict.observed[field])}
                  </Text>
                </FlexItem>
                <FlexItem>
                <Text marginBottom="none" data-testid={`settings-conflict-draft-${field}`}>
                  Your draft: {formatValue(field, draft[field])}
                </Text>
              </FlexItem>
              </Flex>
            ))}
            {converged ? (
              <Text marginBottom="small">
                This is already set to your draft — no action needed.
              </Text>
            ) : conflict.recovery === 'refresh_required' ? (
              <Text marginBottom="small">
                A stored value could not be displayed safely. Refresh its opaque baseline, then
                reapply your unchanged draft.
              </Text>
            ) : (
              <Text marginBottom="small">
                Review the current values, then refresh the baseline and reapply your unchanged
                draft.
              </Text>
            )}
            <Flex flexGap="8px" flexWrap="wrap">
            {!converged && (
              <FlexItem>
                <Button
                  variant="primary"
                    onClick={onReapply}
                    disabled={busy}
                    isLoading={busy}
                    aria-label="Refresh and reapply my changes"
                  >
                    Refresh and reapply my changes
                  </Button>
              </FlexItem>
            )}
            <FlexItem>
              <Button variant="secondary" onClick={onDismiss} disabled={busy}>
                {converged ? 'Use current value' : 'Dismiss'}
              </Button>
              </FlexItem>
            </Flex>
          </Box>
        </div>
      )}
    </>
  );
}
